import { css } from '@emotion/react';

import { media, screens, space } from '../shared/styles';
import type { ResponsiveValue } from './types';

/**
 * Layout tokens for different container sizes
 */
export const LayoutTokens = {
  containers: {
    sm: screens.sm, // 640px
    md: screens.md, // 768px
    lg: screens.lg, // 1024px
    xl: screens.xl, // 1280px
    content: '960px', // Content-focused layouts
    wide: '1440px', // Marketing pages
    full: '100%', // Full-width layouts
  },

  // Grid-specific spacing
  gutters: {
    sm: space[4], // 16px
    md: space[6], // 24px
    lg: space[8], // 32px
    xl: space[10], // 40px
  },
};

/**
 * Converts a responsive value to CSS styles
 */
export const getResponsiveValue = <T>(value: ResponsiveValue<T>): T => {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const responsiveObj = value as { _?: T; sm?: T; md?: T; lg?: T; xl?: T };
    return responsiveObj._ || (Object.values(responsiveObj)[0] as T);
  }
  return value as T;
};

/**
 * Generates responsive grid column styles
 */
export const generateGridColumns = (columns: ResponsiveValue<number>) => {
  const baseColumns = getResponsiveValue(columns);

  if (typeof columns === 'number') {
    return css`
      grid-template-columns: repeat(${columns}, 1fr);
    `;
  }

  return css`
    grid-template-columns: repeat(${baseColumns}, 1fr);

    ${media.sm} {
      grid-template-columns: repeat(${columns.sm || baseColumns}, 1fr);
    }

    ${media.md} {
      grid-template-columns: repeat(
        ${columns.md || columns.sm || baseColumns},
        1fr
      );
    }

    ${media.lg} {
      grid-template-columns: repeat(
        ${columns.lg || columns.md || columns.sm || baseColumns},
        1fr
      );
    }

    ${media.xl} {
      grid-template-columns: repeat(
        ${columns.xl || columns.lg || columns.md || columns.sm || baseColumns},
        1fr
      );
    }
  `;
};

/**
 * Generates responsive gap styles
 */
export const generateGapStyles = (gap: ResponsiveValue<keyof typeof space>) => {
  const baseGap = getResponsiveValue(gap);

  if (typeof gap === 'string' || typeof gap === 'number') {
    return css`
      gap: ${space[gap as keyof typeof space]};
    `;
  }

  return css`
    gap: ${space[baseGap]};

    ${media.sm} {
      gap: ${space[gap.sm || baseGap]};
    }

    ${media.md} {
      gap: ${space[gap.md || gap.sm || baseGap]};
    }

    ${media.lg} {
      gap: ${space[gap.lg || gap.md || gap.sm || baseGap]};
    }

    ${media.xl} {
      gap: ${space[gap.xl || gap.lg || gap.md || gap.sm || baseGap]};
    }
  `;
};

/**
 * Generates responsive row gap styles
 */
export const generateRowGapStyles = (
  rowGap: ResponsiveValue<keyof typeof space>
) => {
  const baseRowGap = getResponsiveValue(rowGap);

  if (typeof rowGap === 'string' || typeof rowGap === 'number') {
    return css`
      row-gap: ${space[rowGap as keyof typeof space]};
    `;
  }

  return css`
    row-gap: ${space[baseRowGap]};

    ${media.sm} {
      row-gap: ${space[rowGap.sm || baseRowGap]};
    }

    ${media.md} {
      row-gap: ${space[rowGap.md || rowGap.sm || baseRowGap]};
    }

    ${media.lg} {
      row-gap: ${space[rowGap.lg || rowGap.md || rowGap.sm || baseRowGap]};
    }

    ${media.xl} {
      row-gap: ${space[
        rowGap.xl || rowGap.lg || rowGap.md || rowGap.sm || baseRowGap
      ]};
    }
  `;
};

/**
 * Generates responsive column gap styles
 */
export const generateColumnGapStyles = (
  columnGap: ResponsiveValue<keyof typeof space>
) => {
  const baseColumnGap = getResponsiveValue(columnGap);

  if (typeof columnGap === 'string' || typeof columnGap === 'number') {
    return css`
      column-gap: ${space[columnGap as keyof typeof space]};
    `;
  }

  return css`
    column-gap: ${space[baseColumnGap]};

    ${media.sm} {
      column-gap: ${space[columnGap.sm || baseColumnGap]};
    }

    ${media.md} {
      column-gap: ${space[columnGap.md || columnGap.sm || baseColumnGap]};
    }

    ${media.lg} {
      column-gap: ${space[
        columnGap.lg || columnGap.md || columnGap.sm || baseColumnGap
      ]};
    }

    ${media.xl} {
      column-gap: ${space[
        columnGap.xl ||
          columnGap.lg ||
          columnGap.md ||
          columnGap.sm ||
          baseColumnGap
      ]};
    }
  `;
};

/**
 * Generates responsive column span styles
 */
export const generateColumnSpan = (span: ResponsiveValue<number | 'auto'>) => {
  const baseSpan = getResponsiveValue(span);

  if (typeof span === 'string' || typeof span === 'number') {
    return css`
      grid-column: ${span === 'auto' ? 'auto' : `span ${span}`};
    `;
  }

  return css`
    grid-column: ${baseSpan === 'auto' ? 'auto' : `span ${baseSpan}`};

    ${media.sm} {
      grid-column: ${(span.sm || baseSpan) === 'auto'
        ? 'auto'
        : `span ${span.sm || baseSpan}`};
    }

    ${media.md} {
      grid-column: ${(span.md || span.sm || baseSpan) === 'auto'
        ? 'auto'
        : `span ${span.md || span.sm || baseSpan}`};
    }

    ${media.lg} {
      grid-column: ${(span.lg || span.md || span.sm || baseSpan) === 'auto'
        ? 'auto'
        : `span ${span.lg || span.md || span.sm || baseSpan}`};
    }

    ${media.xl} {
      grid-column: ${(span.xl || span.lg || span.md || span.sm || baseSpan) ===
      'auto'
        ? 'auto'
        : `span ${span.xl || span.lg || span.md || span.sm || baseSpan}`};
    }
  `;
};

/**
 * Generates responsive align items styles
 */
export const generateAlignItems = (
  alignItems: ResponsiveValue<'start' | 'end' | 'center' | 'stretch'>
) => {
  const baseAlign = getResponsiveValue(alignItems);

  if (typeof alignItems === 'string') {
    return css`
      align-items: ${alignItems};
    `;
  }

  return css`
    align-items: ${baseAlign};

    ${media.sm} {
      align-items: ${alignItems.sm || baseAlign};
    }

    ${media.md} {
      align-items: ${alignItems.md || alignItems.sm || baseAlign};
    }

    ${media.lg} {
      align-items: ${alignItems.lg ||
      alignItems.md ||
      alignItems.sm ||
      baseAlign};
    }

    ${media.xl} {
      align-items: ${alignItems.xl ||
      alignItems.lg ||
      alignItems.md ||
      alignItems.sm ||
      baseAlign};
    }
  `;
};

/**
 * Generates responsive justify items styles
 */
export const generateJustifyItems = (
  justifyItems: ResponsiveValue<'start' | 'end' | 'center' | 'stretch'>
) => {
  const baseJustify = getResponsiveValue(justifyItems);

  if (typeof justifyItems === 'string') {
    return css`
      justify-items: ${justifyItems};
    `;
  }

  return css`
    justify-items: ${baseJustify};

    ${media.sm} {
      justify-items: ${justifyItems.sm || baseJustify};
    }

    ${media.md} {
      justify-items: ${justifyItems.md || justifyItems.sm || baseJustify};
    }

    ${media.lg} {
      justify-items: ${justifyItems.lg ||
      justifyItems.md ||
      justifyItems.sm ||
      baseJustify};
    }

    ${media.xl} {
      justify-items: ${justifyItems.xl ||
      justifyItems.lg ||
      justifyItems.md ||
      justifyItems.sm ||
      baseJustify};
    }
  `;
};
